import React, { useEffect, useState } from "react";
import logger from "../../logger";
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  AddRegular,
  TableAddRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import { fetchAllMetrics, fetchSelectedMetric } from "./metricService";
import { useMetricManagementStyles } from "./MetricManagement.styles";

const MetricManagement = () => {
  const [excelData, setExcelData] = useState("");
  const [excelError, setExcelError] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [selectedMetricId, setSelectedMetricId] = useState("");
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [metricDataRange, setMetricDataRange] = useState(null);
  const styles = useMetricManagementStyles();

  const metricNameToIdMap = React.useMemo(() => {
    const map = {};
    metrics.forEach((metric) => {
      const metricName = metric.name || metric.id;
      map[metricName] = metric.id;
    });
    return map;
  }, [metrics]);

  const selectedMetricDisplayName = React.useMemo(() => {
    if (!selectedMetricId) return undefined;
    const metric = metrics.find((m) => m.id === selectedMetricId);
    return metric ? metric.name || metric.id : undefined;
  }, [selectedMetricId, metrics]);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);
      const metricsData = await fetchAllMetrics();
      setMetrics(metricsData);
    } catch (error) {
      logger.error("Error loading metrics:", error);
      setExcelData("Error loading metrics: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setMetricsLoading(false);
    }
  };

  const insertMetricDataToExcel = async (metricsData) => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        if (!metricsData || metricsData.length === 0) {
          throw new Error("No metric data to insert");
        }

        // Selected cell
        const selectedRange = context.workbook.getSelectedRange();
        selectedRange.load("rowIndex, columnIndex");
        await context.sync();

        const startRow = selectedRange.rowIndex + 1;
        const startColIndex = selectedRange.columnIndex;

        // Headers
        const headers = ["Metric Id", "Custom Id", "Metric Name"];
        const result = [headers];

        // Rows
        for (let i = 0; i < metricsData.length; i++) {
          const metric = metricsData[i];
          if (!metric) continue;

          result.push([metric.id || "", metric.reference || "", metric.name || ""]);
        }

        const numRows = result.length;
        const numCols = headers.length;

        // Column letter helper
        const getColumnLetter = (colNum) => {
          let letter = "";
          while (colNum > 0) {
            colNum--;
            letter = String.fromCharCode(65 + (colNum % 26)) + letter;
            colNum = Math.floor(colNum / 26);
          }
          return letter;
        };

        const startCol = getColumnLetter(startColIndex + 1);
        const endCol = getColumnLetter(startColIndex + numCols);
        const endRow = startRow + numRows - 1;

        // Write data
        const dataRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);
        dataRange.values = result;

        // Header styling
        const headerRange = sheet.getRange(`${startCol}${startRow}:${endCol}${startRow}`);
        headerRange.format.fill.color = "#1aebff";
        headerRange.format.font.color = "black";
        headerRange.format.font.bold = true;

        // Autofit only first two columns
        const firstCol = getColumnLetter(startColIndex + 1);
        const secondCol = getColumnLetter(startColIndex + 2);
        sheet.getRange(`${firstCol}:${secondCol}`).format.autofitColumns();

        // Metric Name column (3rd column)
        const metricNameColIndex = startColIndex + 2;
        const metricNameColLetter = getColumnLetter(metricNameColIndex + 1);
        const metricNameRange = sheet.getRange(
          `${metricNameColLetter}${startRow}:${metricNameColLetter}${endRow}`
        );

        // FIXED WIDTH + WRAP TEXT
        metricNameRange.format.columnWidth = 250;
        metricNameRange.format.wrapText = true;
        metricNameRange.format.verticalAlignment = Excel.VerticalAlignment.top;
        metricNameRange.format.autofitRows();

        await context.sync();

        // Save range info (if needed later)
        setMetricDataRange({
          startCol,
          endCol,
          startRow,
          endRow,
        });

        setExcelData("Metric data successfully inserted into Excel!");
        setExcelError(false);

        setTimeout(() => {
          setExcelData("");
        }, 3000);
      });
    } catch (error) {
      logger.error("Error inserting metric data to Excel:", error);
      setExcelData("Error inserting metric data to Excel: " + (error.message || "Unknown error"));
      setExcelError(true);
    }
  };

  const clearMetricDataRange = async () => {
    try {
      if (!metricDataRange) {
        setExcelData("No metric data range found to clear.");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
        return;
      }

      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const { startCol, endCol, startRow, endRow } = metricDataRange;
        const fullRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);

        fullRange.clear(Excel.ClearApplyTo.all);

        await context.sync();
      });
    } catch (error) {
      logger.error("Error clearing metric data range:", error);
      throw error;
    }
  };

  const handleClearAll = async () => {
    setSelectedMetricId("");
    setDropdownKey((prev) => prev + 1);

    try {
      await clearMetricDataRange();
      setMetricDataRange(null);
      setExcelData("Metric data cleared successfully!");
      setExcelError(false);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } catch (error) {
      logger.error("Error clearing metric data:", error);
      setExcelData("Error clearing metric data: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    }
  };

  const handleSelectedMetricGet = async () => {
    if (!selectedMetricId) {
      setExcelData("Please select a metric first");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    try {
      setMetricsLoading(true);
      const metricData = await fetchSelectedMetric(selectedMetricId);
      if (metricData && typeof metricData === "object" && !Array.isArray(metricData)) {
        await insertMetricDataToExcel([metricData]);
      } else {
        setExcelData("No metric data found");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
      }
    } catch (error) {
      setExcelData("Error fetching selected metric: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleInsertAllMetrics = async () => {
    if (metrics.length === 0) {
      setExcelData("No metrics available. Please fetch metrics first.");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    await insertMetricDataToExcel(metrics);
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          Select a Metric:
        </Text>
        <Dropdown
          key={dropdownKey}
          placeholder={metricsLoading ? "Loading metrics..." : "Select a metric"}
          value={selectedMetricDisplayName || ""}
          disabled={metricsLoading}
          onOptionSelect={(event, data) => {
            const selectedMetricName = data.optionValue || "";
            const metricId = metricNameToIdMap[selectedMetricName] || "";
            setSelectedMetricId(metricId);
          }}
          className={styles.dropdown}
        >
          {metrics.map((metric) => {
            const metricName = metric.name || metric.id;
            return (
              <Option key={metric.id} value={metricName}>
                {metricName}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <div className={styles.buttonsContainer}>
        <Tooltip
          withArrow
          content={{ children: "Refetch Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={loadMetrics}
            disabled={metricsLoading}
            className={styles.refetchButton}
            icon={<ArrowSyncRegular />}
          />
        </Tooltip>
        <Tooltip
          withArrow
          content={{ children: "Insert Metric", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="primary"
            onClick={handleSelectedMetricGet}
            disabled={metricsLoading || !selectedMetricId}
            className={styles.insertButton}
            icon={<AddRegular />}
          />
        </Tooltip>
        {metrics.length > 0 && (
          <Tooltip
            withArrow
            content={{ children: "Insert All Metric", className: styles.tooltip }}
            relationship="label"
            positioning="below"
          >
            <Button
              appearance="primary"
              onClick={handleInsertAllMetrics}
              disabled={metricsLoading}
              className={styles.insertAllButton}
              icon={<TableAddRegular />}
            />
          </Tooltip>
        )}
        <Tooltip
          withArrow
          content={{ children: "Clear Metric Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={handleClearAll}
            disabled={metricsLoading}
            className={styles.clearButton}
            icon={<DeleteRegular />}
          />
        </Tooltip>
      </div>

      {excelData && (
        <MessageBar
          intent={excelError ? "error" : "success"}
          className={excelError ? styles.errorMessage : styles.successMessage}
        >
          <MessageBarBody>{excelData}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default MetricManagement;
